import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { getIssue, createIssueTopic, updateIssueTopic } from "../../api/issuesApi";
import { translateAuthError, API_ORIGIN } from "../../api/authApi";
import { CATEGORY_ORDER, MANIFEST_CATEGORIES, APPENDIX_CATEGORY } from "../../data/publications";
import RichTextEditor from "../../components/RichTextEditor";
import FootnotesEditor from "../../components/FootnotesEditor";
import AdminImageField from "../../components/AdminImageField";
import MetadataFieldsEditor from "../../components/MetadataFieldsEditor";
import { EMPTY_METADATA, normalizeMetadata } from "../../utils/metadata";

// Section C (admin-only): shared create/edit form for a single card
// (topic) within an issue. Mirrors TopicFormPage.js. Presence of the
// :topicId route param decides the mode; the category is editable in
// both modes, defaulting to the ?category= query param on create. Every
// text field has an independent Persian and English half — either half
// can be left entirely empty (a card with no translation yet), but a
// non-empty title needs its byline filled in too, same language side
// only (see usecase.validIssueTopicHalf on the backend).
function IssueTopicFormPage() {
  const { id, topicId } = useParams();
  const [searchParams] = useSearchParams();
  const isEdit = Boolean(topicId);
  const [category, setCategory] = useState(searchParams.get("category") || CATEGORY_ORDER[0]);
  const [titleFa, setTitleFa] = useState("");
  const [titleEn, setTitleEn] = useState("");
  const [descriptionFa, setDescriptionFa] = useState("");
  const [descriptionEn, setDescriptionEn] = useState("");
  const [bodyFa, setBodyFa] = useState("");
  const [bodyEn, setBodyEn] = useState("");
  const [bylineFa, setBylineFa] = useState("");
  const [bylineEn, setBylineEn] = useState("");
  const [footnotesFa, setFootnotesFa] = useState([]);
  const [footnotesEn, setFootnotesEn] = useState([]);
  const [file, setFile] = useState(null);
  const [existingImagePath, setExistingImagePath] = useState("");
  const [removeImage, setRemoveImage] = useState(false);
  const [metadata, setMetadata] = useState(EMPTY_METADATA);
  const [issueNumber, setIssueNumber] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  // Issue number 12 has two extra categories (see MANIFEST_CATEGORIES) —
  // fetched here in both create and edit mode just to know whether to
  // offer them, independent of the edit-only topic-prefill below.
  useEffect(() => {
    getIssue(id)
      .then((iss) => setIssueNumber(iss.number))
      .catch(() => {});
  }, [id]);

  useEffect(() => {
    if (!isEdit) return;
    getIssue(id)
      .then((iss) => {
        const topic = iss.topics.find((t) => t.id === Number(topicId));
        if (!topic) {
          setError("کارت موردنظر یافت نشد.");
          return;
        }
        setCategory(topic.category);
        setTitleFa(topic.title_fa || "");
        setTitleEn(topic.title_en || "");
        setDescriptionFa(topic.description_fa || "");
        setDescriptionEn(topic.description_en || "");
        setBodyFa(topic.body_fa || "");
        setBodyEn(topic.body_en || "");
        setBylineFa(topic.byline_fa || "");
        setBylineEn(topic.byline_en || "");
        setFootnotesFa(topic.footnotes_fa || []);
        setFootnotesEn(topic.footnotes_en || []);
        setExistingImagePath(topic.image_path || "");
        setMetadata(normalizeMetadata(topic.metadata));
      })
      .catch((err) => setError(translateAuthError(err.message)))
      .finally(() => setLoading(false));
  }, [id, topicId, isEdit]);

  const categoryOptions =
    issueNumber === 12 ? [...CATEGORY_ORDER, ...MANIFEST_CATEGORIES, APPENDIX_CATEGORY] : CATEGORY_ORDER;

  async function handleSubmit(event) {
    event.preventDefault();
    setError("");
    if (!titleFa.trim() && !titleEn.trim()) {
      setError("حداقل یکی از دو زبان باید عنوان و نام نویسنده داشته باشد.");
      return;
    }
    if (Boolean(titleFa.trim()) !== Boolean(bylineFa.trim())) {
      setError("عنوان و نام نویسندهٔ فارسی باید همزمان پر یا خالی باشند.");
      return;
    }
    if (Boolean(titleEn.trim()) !== Boolean(bylineEn.trim())) {
      setError("عنوان و نام نویسندهٔ انگلیسی باید همزمان پر یا خالی باشند.");
      return;
    }

    const formData = new FormData();
    formData.append("category", category);
    formData.append("title_fa", titleFa);
    formData.append("title_en", titleEn);
    formData.append("description_fa", descriptionFa);
    formData.append("description_en", descriptionEn);
    formData.append("body_fa", bodyFa);
    formData.append("body_en", bodyEn);
    formData.append("byline_fa", bylineFa);
    formData.append("byline_en", bylineEn);
    for (const note of footnotesFa.map((f) => f.trim()).filter(Boolean)) {
      formData.append("footnotes_fa", note);
    }
    for (const note of footnotesEn.map((f) => f.trim()).filter(Boolean)) {
      formData.append("footnotes_en", note);
    }
    formData.append("metadata", JSON.stringify(metadata));
    if (file) {
      formData.append("image", file);
    } else if (removeImage) {
      formData.append("remove_image", "true");
    }

    setSubmitting(true);
    try {
      if (isEdit) {
        await updateIssueTopic(token, topicId, formData);
      } else {
        await createIssueTopic(token, id, formData);
      }
      navigate(`/admin/issues/${id}/topics`);
    } catch (err) {
      setError(translateAuthError(err.message));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="admin-page">
      <Link to={`/admin/issues/${id}/topics`} className="back-link">
        ← بازگشت به کارت‌ها
      </Link>

      {loading ? (
        <p>در حال بارگذاری...</p>
      ) : (
        <form className="auth-card admin-article-form" onSubmit={handleSubmit}>
          <span className="corner corner-tr" />
          <span className="corner corner-tl" />
          <span className="corner corner-br" />
          <span className="corner corner-bl" />

          <h2>{isEdit ? "ویرایش کارت" : "کارت جدید"}</h2>

          {error && <p className="error">{error}</p>}

          <div className="auth-field">
            <label htmlFor="category">دسته</label>
            <select id="category" value={category} onChange={(e) => setCategory(e.target.value)}>
              {categoryOptions.map((c) => (
                <option value={c} key={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          <div className="bilingual-section">
            <h3 className="bilingual-section-heading">فارسی</h3>

            <div className="auth-field">
              <label htmlFor="title-fa">عنوان</label>
              <input id="title-fa" type="text" value={titleFa} onChange={(e) => setTitleFa(e.target.value)} dir="rtl" />
            </div>

            <div className="auth-field">
              <label htmlFor="description-fa">توضیح کوتاه (روی کارت نمایش داده می‌شود، اختیاری)</label>
              <textarea id="description-fa" rows={3} value={descriptionFa} onChange={(e) => setDescriptionFa(e.target.value)} dir="rtl" />
            </div>

            <div className="auth-field">
              <label htmlFor="byline-fa">نام نویسنده</label>
              <input id="byline-fa" type="text" value={bylineFa} onChange={(e) => setBylineFa(e.target.value)} dir="rtl" />
            </div>

            <div className="auth-field">
              <label htmlFor="body-fa">متن کامل (در صفحهٔ اختصاصی کارت نمایش داده می‌شود)</label>
              <RichTextEditor id="body-fa" value={bodyFa} onChange={setBodyFa} />
            </div>

            <div className="auth-field">
              <label>پانویس‌ها (اختیاری)</label>
              <FootnotesEditor footnotes={footnotesFa} onChange={setFootnotesFa} />
            </div>
          </div>

          <div className="bilingual-section">
            <h3 className="bilingual-section-heading">English</h3>

            <div className="auth-field">
              <label htmlFor="title-en">Title</label>
              <input id="title-en" type="text" value={titleEn} onChange={(e) => setTitleEn(e.target.value)} dir="ltr" />
            </div>

            <div className="auth-field">
              <label htmlFor="description-en">Short description (shown on the card, optional)</label>
              <textarea id="description-en" rows={3} value={descriptionEn} onChange={(e) => setDescriptionEn(e.target.value)} dir="ltr" />
            </div>

            <div className="auth-field">
              <label htmlFor="byline-en">Author name</label>
              <input id="byline-en" type="text" value={bylineEn} onChange={(e) => setBylineEn(e.target.value)} dir="ltr" />
            </div>

            <div className="auth-field">
              <label htmlFor="body-en">Full body (shown on the card's own page)</label>
              <RichTextEditor id="body-en" value={bodyEn} onChange={setBodyEn} />
            </div>

            <div className="auth-field">
              <label>Footnotes (optional)</label>
              <FootnotesEditor footnotes={footnotesEn} onChange={setFootnotesEn} />
            </div>
          </div>

          <AdminImageField
            label="تصویر کارت (اختیاری)"
            imageUrl={existingImagePath ? `${API_ORIGIN}${existingImagePath}` : ""}
            file={file}
            removed={removeImage}
            onFileChange={(chosen) => {
              setFile(chosen);
              if (chosen) setRemoveImage(false);
            }}
            onRemove={() => setRemoveImage(true)}
          />

          <div className="auth-field">
            <label>شناسه (اختیاری)</label>
            <MetadataFieldsEditor metadata={metadata} onChange={setMetadata} />
          </div>

          <button type="submit" className="auth-submit" disabled={submitting}>
            {submitting ? "در حال ذخیره..." : isEdit ? "ذخیرهٔ تغییرات" : "افزودن کارت"}
          </button>
        </form>
      )}
    </div>
  );
}

export default IssueTopicFormPage;
